import { useIntl } from '@capsicum/common';
import {
	type NCWSendAssetSelectorSearchResult,
	type NCWSendAssetType,
	type NCWTokenBalanceType
} from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';

import { ncwSendAssetAtom } from '../../data-access';
import { useResetTransferState } from '../useResetTransferState';

const useSearchKeywordMatching = ({
	chilizAsset,
	searchKeyword,
	tokens
}: {
	chilizAsset: NCWSendAssetType | undefined;
	searchKeyword: string;
	tokens: Array<NCWSendAssetType>;
}) =>
	useMemo(() => {
		const lowerCaseSearchKeyword = searchKeyword.toLocaleLowerCase();
		const assetMatchesSearchKeyword = (asset: NCWSendAssetType) =>
			!!asset?.assetName?.toLocaleLowerCase().includes(lowerCaseSearchKeyword);

		const chilizSearchResult: NCWSendAssetSelectorSearchResult | undefined = chilizAsset && {
			asset: chilizAsset,
			show: assetMatchesSearchKeyword(chilizAsset)
		};

		let foundFanTokensMatchingSearchKeyword = false;
		const fanTokenSearchResults: Array<NCWSendAssetSelectorSearchResult> = tokens.map(asset => {
			const show = assetMatchesSearchKeyword(asset);

			foundFanTokensMatchingSearchKeyword ||= show;

			return {
				asset,
				show
			};
		});

		return { chilizSearchResult, fanTokenSearchResults, foundFanTokensMatchingSearchKeyword };
	}, [chilizAsset, tokens, searchKeyword]);

const useChilizAsset = (chilizBalance?: NCWTokenBalanceType) => {
	return {
		chilizAsset: useMemo(() => {
			const hasEnoughCHZAvailabletoWithdraw = Number(chilizBalance?.amount) > 0;

			if (!hasEnoughCHZAvailabletoWithdraw) {
				return;
			}

			return {
				assetId: chilizBalance?.currencyId,
				assetName: 'Chiliz',
				assetType: 'chiliz',
				formattedBalance: chilizBalance?.tokenCardData.usableFormattedDefaultBalance,
				shortCode: 'CHZ',
				symbol: '$CHZ',
				usableAmount: chilizBalance?.usableAmount || 0
			} satisfies NCWSendAssetType;
		}, [chilizBalance])
	};
};

export const useNCWSendAssetSelector = (
	isLoading: boolean,
	tokenBalances?: Array<NCWTokenBalanceType>,
	chilizBalance?: NCWTokenBalanceType,
	isV2Migrating = false
) => {
	const { $t } = useIntl();
	const pageTitle = $t('ncWallet.send.assetSelector.title');
	const searchPlaceholder = $t('ncWallet.send.assetSelector.searchInputPlaceholder');
	const fanTokenListTitle = $t('ncWallet.send.assetSelector.fanTokenListTitle');
	const continueBtnText = $t('ncWallet.send.assetSelector.continue');

	// Reset the selection and other state when entering the asset selection page
	useResetTransferState();

	// This atom keeps track of the Asset data that the user selects
	const [sendAsset, setSendAsset] = useAtom(ncwSendAssetAtom);

	// This state keeps track of the search input keyword
	const [searchKeyword, setSearchKeyword] = useState('');

	// tokens is a Array consisting only of non-chiliz tokens
	const tokens = useMemo(
		() =>
			isLoading
				? []
				: (tokenBalances ?? []).reduce<Array<NCWSendAssetType>>((tokens, tb) => {
						if (isV2Migrating && tb.type === 'nonDecimalVoteToken') return tokens;

						tokens.push({
							assetId: tb.currencyId,
							assetName: tb.tokenCardData.title,
							assetType: 'token',
							decimalDetails: tb.decimalDetails,
							formattedBalance: tb.tokenCardData.usableTokenBalance,
							imageUri: tb.tokenCardData.tokenImgUrl,
							shortCode: tb.shortCode,
							smartContractAddress: tb.smartContractAddress,
							symbol: tb.symbol,
							usableAmount: tb.usableAmount
						});

						return tokens;
					}, []),
		[tokenBalances, isV2Migrating, isLoading]
	);

	const { chilizAsset } = useChilizAsset(chilizBalance);

	const searchResults = useSearchKeywordMatching({ chilizAsset, searchKeyword, tokens });

	return {
		...searchResults,
		intlText: {
			continueBtnText,
			fanTokenListTitle,
			pageTitle,
			searchPlaceholder
		},
		isLoading,
		onSearchChange: setSearchKeyword,
		searchKeyword,
		sendAsset,
		setSendAsset
	};
};
